/**
 * Graphiques Plotly pour le dimensionnement PV : barres (séries par fréquence) et
 * courbes pour le bloc « Profil de production (saison) » : profils **horaires** sur
 * une semaine type **du lundi au dimanche** (168 créneaux : 7 × 24 h), avec moyenne
 * des points tombant sur le même créneau (jour ISO × heure).
 */

import type { DataChartLegendKpiItem } from "@modules/data/components/data/chart/dataChartLegendKpi.types"
import type { PlotlyGraph } from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import {
    applyHomeysStandardTraceHover,
    buildHomeysBarHoverTemplateYAndTraceName,
    buildHomeysStandardPlotlyLayout,
    extractTooltipUnitFromAxisLabel,
} from "@modules/data/utils/plotly"
import { formatNumber } from "@core/helpers"
import moment from "moment"
import type { PvChartBlock, PvChartPoint } from "../types/pv-common.types"

export type PvDimensionnementPlotlyLabels = {
    consommation: string
    production: string
    autoconsommation: string
    surplus: string
}

/** Couleurs des barres « Consommation, production, autoconsommation » (alignées sur Plotly). */
const DIM_MAIN_BAR_COLORS = {
    consommation: "#9333ea",
    production: "#0ea5e9",
    autoconsommation: "#16a34a",
    surplus: "#f59e0b",
} as const

/**
 * Légendes du profil saison (semaine type) — alignées sur le legacy `PVWeekly` /
 * `component.pv_weekly.chart` (consommation hebdo, installation totale, pans).
 */
export type PvDimensionnementSeasonProfileLineLabels = {
    /** ex. « Consommation Hebdomadaire » */
    weeklyConsumption: string
    /** ex. « Installation totale » */
    totalInstallation: string
    /** ex. « Pan de toit » — libellé trace = `${segmentPrefix} ${n}` */
    segmentPrefix: string
}

/** Couleurs profil hebdo legacy (ChartMixedLinear / `PVWeekly`). */
const SEASON_LINE_COLOR_CONSOMMATION = "#db62f4"
const SEASON_LINE_COLOR_PRODUCTION_TOTAL = "#399ebe"

/** Couleurs stables par pan (le legacy utilisait `getRandomColors` non déterministe). */
const SEASON_LINE_COLORS_SEGMENTS: readonly string[] = [
    "#e11d48",
    "#ea580c",
    "#ca8a04",
    "#16a34a",
    "#0d9488",
    "#2563eb",
    "#7c3aed",
    "#db2777",
]

/** Libellés des jours (lundi → dimanche), graduations de l’axe X (un tick par jour). */
const WEEKDAY_LABELS_FR = [
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
    "Dimanche",
] as const

/** Nombre de créneaux horaires sur une semaine complète. */
const WEEK_HOURLY_SLOTS = 168

/**
 * Indice 0..167 à partir d’une date (horodatage ISO).
 * (jour ISO 1–7) − 1) × 24 + heure locale 0..23.
 */
function getWeekHourlySlotFromDateString(x: string): number | null {
    const m = moment(x)
    if (!m.isValid()) return null
    const wd = m.isoWeekday()
    const h = m.hours()
    if (wd < 1 || wd > 7) return null
    const slot = (wd - 1) * 24 + h
    return slot >= 0 && slot < WEEK_HOURLY_SLOTS ? slot : null
}

/**
 * Résout le créneau 0..167 : nombre entier (API profil saison), chiffres en string,
 * ou repli sur une date ISO. Ne pas utiliser `moment(n)` pour un entier : en ms ce
 * serait presque le même instant pour 0..167.
 */
function resolveWeekHourlySlot(x: unknown): number | null {
    if (typeof x === "number" && Number.isFinite(x)) {
        const s = Math.trunc(x)
        if (s >= 0 && s < WEEK_HOURLY_SLOTS) return s
        return null
    }
    if (typeof x === "string") {
        const t = x.trim()
        if (/^\d+$/.test(t)) {
            const s = parseInt(t, 10)
            if (s >= 0 && s < WEEK_HOURLY_SLOTS) return s
        }
        return getWeekHourlySlotFromDateString(x)
    }
    return null
}

function formatWeekHourlySlotLabel(slot: number): string {
    const dayIndex = Math.floor(slot / 24)
    const hour = slot % 24
    const day =
        dayIndex >= 0 && dayIndex < WEEKDAY_LABELS_FR.length
            ? WEEKDAY_LABELS_FR[dayIndex]
            : "—"
    return `${day} · ${String(hour).padStart(2, "0")} h`
}

function barTrace(
    points: PvChartPoint[] | undefined,
    yKey: "y" | "y1" | "y2",
    name: string,
    color: string,
    hovertemplate: string
): object | null {
    if (!Array.isArray(points) || points.length === 0) return null
    const xs = points.map((p) => p.x)
    const ys = points.map((p) => getYFromPoint(p, yKey))
    if (!ys.some((v) => v != null && Number.isFinite(v))) return null
    return {
        type: "bar",
        name,
        x: xs,
        y: ys,
        marker: { color },
        hovertemplate,
    }
}

/**
 * Extrait l’ordonnée pour une clé (y, y1, y2, ou y_<id> côté API).
 */
function getYFromPoint(
    p: PvChartPoint,
    yKey: "y" | "y1" | "y2" | string
): number | null {
    if (yKey === "y") {
        return typeof p.y === "number" && Number.isFinite(p.y) ? p.y : null
    }
    if (yKey === "y1") {
        return typeof p.y1 === "number" && Number.isFinite(p.y1) ? p.y1 : null
    }
    if (yKey === "y2") {
        return typeof p.y2 === "number" && Number.isFinite(p.y2) ? p.y2 : null
    }
    const raw = (p as Record<string, unknown>)[yKey]
    return typeof raw === "number" && Number.isFinite(raw) ? raw : null
}

/**
 * Agrège les points sur 168 créneaux (jour ISO × heure) et produit y + texte survol.
 */
function aggregateToWeekHourlyProfile(
    points: PvChartPoint[] | undefined,
    yKey: "y" | "y1" | "y2" | string
): { y: (number | null)[]; text: string[] } | null {
    if (!Array.isArray(points) || points.length === 0) return null
    const sums = new Array(WEEK_HOURLY_SLOTS).fill(0)
    const counts = new Array(WEEK_HOURLY_SLOTS).fill(0)
    for (const p of points) {
        const slot = resolveWeekHourlySlot(p.x)
        if (slot == null) continue
        const v = getYFromPoint(p, yKey)
        if (v == null) continue
        sums[slot] += v
        counts[slot] += 1
    }
    const y: (number | null)[] = []
    const text: string[] = []
    for (let i = 0; i < WEEK_HOURLY_SLOTS; i++) {
        y.push(counts[i] > 0 ? sums[i] / counts[i] : null)
        text.push(formatWeekHourlySlotLabel(i))
    }
    if (!y.some((v) => v != null && Number.isFinite(v))) return null
    return { y, text }
}

/**
 * Courbes sur 0..167 (lundi 0h → dimanche 23h), une valeur moyenne par créneau horaire.
 */
function lineTraceWeekHourlyProfile(
    points: PvChartPoint[] | undefined,
    yKey: "y" | "y1" | "y2" | string,
    name: string,
    color: string
): object | null {
    const series = aggregateToWeekHourlyProfile(points, yKey)
    if (!series) return null
    const x = Array.from({ length: WEEK_HOURLY_SLOTS }, (_, i) => i)
    const yTooltipUnit = extractTooltipUnitFromAxisLabel("kWh")
    const unitPart = yTooltipUnit.trim() ? ` ${yTooltipUnit.trim()}` : ""
    const hovertemplate = `<b>%{y:.2f}</b>${unitPart}<br>%{text}<br><b>%{fullData.name}</b><extra></extra>`
    return {
        type: "scatter",
        mode: "lines+markers",
        name,
        x,
        y: series.y,
        text: series.text,
        line: { color, width: 2 },
        marker: { color, size: 3 },
        connectgaps: false,
        hovertemplate,
    }
}

/**
 * Indique si au moins un `y` fini existe (même critère que {@link barTrace} pour `y`).
 */
function hasAnyFiniteY(points: PvChartPoint[] | undefined): boolean {
    if (!Array.isArray(points) || points.length === 0) return false
    return points.some((p) => typeof p.y === "number" && Number.isFinite(p.y))
}

/**
 * Somme les `y` finis d’une série de points (même logique que les barres Plotly).
 */
function sumFiniteY(points: PvChartPoint[] | undefined): number {
    if (!Array.isArray(points)) return 0
    return points.reduce((acc, p) => {
        const v = typeof p.y === "number" && Number.isFinite(p.y) ? p.y : 0
        return acc + v
    }, 0)
}

/**
 * Somme les `y1` finis (autoconsommation sur le sous-graphique combiné).
 */
function sumFiniteY1(points: PvChartPoint[] | undefined): number {
    if (!Array.isArray(points)) return 0
    return points.reduce((acc, p) => {
        const v = typeof p.y1 === "number" && Number.isFinite(p.y1) ? p.y1 : 0
        return acc + v
    }, 0)
}

/**
 * Somme les `y2` finis (surplus sur le sous-graphique combiné).
 */
function sumFiniteY2(points: PvChartPoint[] | undefined): number {
    if (!Array.isArray(points)) return 0
    return points.reduce((acc, p) => {
        const v = typeof p.y2 === "number" && Number.isFinite(p.y2) ? p.y2 : 0
        return acc + v
    }, 0)
}

const DIM_MAIN_KWH_FORMAT = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: "decimal" as const,
}

/**
 * Totaux par tracé sur la période du bloc courant (même sous-graphiques que
 * {@link buildPvDimensionnementPlotlyGraph}), pour affichage type légende KPI legacy.
 *
 * @param chartBlock Bloc `dimensionnement_pv_*` fusionné pour la fréquence affichée
 * @param labels Libellés des séries (i18n côté appelant)
 * @returns Liste ordonnée : consommation, production, autoconsommation, surplus (entrées omises si aucune donnée exploitable)
 */
export function extractPvDimensionnementMainChartLegendKpiItems(
    chartBlock: PvChartBlock | null | undefined,
    labels: PvDimensionnementPlotlyLabels
): DataChartLegendKpiItem[] {
    if (!chartBlock?.subcharts) return []
    const subs = chartBlock.subcharts
    const unit = "kWh"
    const items: DataChartLegendKpiItem[] = []

    const consoPts = subs.consommation?.content?.points
    if (hasAnyFiniteY(consoPts)) {
        const total = sumFiniteY(consoPts)
        items.push({
            name: labels.consommation,
            color: DIM_MAIN_BAR_COLORS.consommation,
            value: formatNumber(total, DIM_MAIN_KWH_FORMAT),
            unit,
        })
    }

    const prodPts = subs.production_pv?.content?.points
    if (hasAnyFiniteY(prodPts)) {
        const total = sumFiniteY(prodPts)
        items.push({
            name: labels.production,
            color: DIM_MAIN_BAR_COLORS.production,
            value: formatNumber(total, DIM_MAIN_KWH_FORMAT),
            unit,
        })
    }

    const autoPts = subs.autoconsommation_surproduction_pv?.content?.points
    if (Array.isArray(autoPts) && autoPts.length > 0) {
        const t1 = sumFiniteY1(autoPts)
        const t2 = sumFiniteY2(autoPts)
        const hasY1 = autoPts.some(
            (p) => typeof p.y1 === "number" && Number.isFinite(p.y1)
        )
        const hasY2 = autoPts.some(
            (p) => typeof p.y2 === "number" && Number.isFinite(p.y2)
        )
        if (hasY1) {
            items.push({
                name: labels.autoconsommation,
                color: DIM_MAIN_BAR_COLORS.autoconsommation,
                value: formatNumber(t1, DIM_MAIN_KWH_FORMAT),
                unit,
            })
        }
        if (hasY2) {
            items.push({
                name: labels.surplus,
                color: DIM_MAIN_BAR_COLORS.surplus,
                value: formatNumber(t2, DIM_MAIN_KWH_FORMAT),
                unit,
            })
        }
    }

    return items
}

/**
 * Barres empilées / groupées par date (toutes fréquences) : consommation, production, autoconsommation, surplus.
 */
export function buildPvDimensionnementPlotlyGraph(
    chartBlock: PvChartBlock | null | undefined,
    labels: PvDimensionnementPlotlyLabels
): PlotlyGraph | null {
    if (!chartBlock?.subcharts) return null
    const subs = chartBlock.subcharts
    const yLabel = "kWh"
    const yTooltipUnit = extractTooltipUnitFromAxisLabel(yLabel)
    const hovertemplate = buildHomeysBarHoverTemplateYAndTraceName({
        unit: yTooltipUnit,
    })

    const traces: object[] = []
    const conso = barTrace(
        subs.consommation?.content?.points,
        "y",
        labels.consommation,
        DIM_MAIN_BAR_COLORS.consommation,
        hovertemplate
    )
    if (conso) traces.push(conso)
    const prod = barTrace(
        subs.production_pv?.content?.points,
        "y",
        labels.production,
        DIM_MAIN_BAR_COLORS.production,
        hovertemplate
    )
    if (prod) traces.push(prod)
    const autoPts = subs.autoconsommation_surproduction_pv?.content?.points
    const auto1 = barTrace(
        autoPts,
        "y1",
        labels.autoconsommation,
        DIM_MAIN_BAR_COLORS.autoconsommation,
        hovertemplate
    )
    const auto2 = barTrace(
        autoPts,
        "y2",
        labels.surplus,
        DIM_MAIN_BAR_COLORS.surplus,
        hovertemplate
    )
    if (auto1) traces.push(auto1)
    if (auto2) traces.push(auto2)

    if (traces.length === 0) return null

    const data = applyHomeysStandardTraceHover(traces, {
        yAxisLabel: yLabel,
    })

    const layoutMerged = buildHomeysStandardPlotlyLayout(
        {
            autosize: true,
            barmode: "group",
            bargap: 0.18,
            showlegend: true,
            legend: { orientation: "h", y: -0.22 },
            margin: { t: 16, r: 16, b: 80, l: 56 },
            xaxis: {},
            yaxis: { rangemode: "tozero" },
        },
        {
            x_label: "Date",
            y_label: yLabel,
            name: chartBlock.name,
        },
        { dateXAxisFr: true }
    )

    return {
        data,
        layout: layoutMerged,
    }
}

/**
 * Courbes d’un profil **horaire** sur la **semaine type lundi → dimanche** (données
 * `dimensionnement_pv_hourly_saison_*`), comme le legacy `PVWeekly` : consommation
 * hebdomadaire, production totale (`y`) et, par pan, `y_<id>`. Pas d’autoconsommation
 * / surplus sur ce graphique.
 *
 * Axe X : 168 heures (0 = lun. 0h … 167 = dim. 23h) ; moyenne par créneau si besoin.
 */
export function buildPvDimensionnementSeasonHourlyWeekLineGraph(
    chartBlock: PvChartBlock | null | undefined,
    options: {
        labels: PvDimensionnementSeasonProfileLineLabels
        /** Même ordre que `list_params_pv` / `y_<id>` dans l’API. */
        segmentIds: readonly string[]
    }
): PlotlyGraph | null {
    if (!chartBlock?.subcharts) return null
    const subs = chartBlock.subcharts
    const yLabel = "kWh"
    const { labels, segmentIds } = options

    const traces: object[] = []
    const conso = lineTraceWeekHourlyProfile(
        subs.consommation?.content?.points,
        "y",
        labels.weeklyConsumption,
        SEASON_LINE_COLOR_CONSOMMATION
    )
    if (conso) traces.push(conso)
    const prod = lineTraceWeekHourlyProfile(
        subs.production_pv?.content?.points,
        "y",
        labels.totalInstallation,
        SEASON_LINE_COLOR_PRODUCTION_TOTAL
    )
    if (prod) traces.push(prod)
    segmentIds.forEach((id, index) => {
        const yKey = `y_${id}`
        const name = `${labels.segmentPrefix} ${index + 1}`.trim()
        const color =
            SEASON_LINE_COLORS_SEGMENTS[
                index % SEASON_LINE_COLORS_SEGMENTS.length
            ]!
        const seg = lineTraceWeekHourlyProfile(
            subs.production_pv?.content?.points,
            yKey,
            name,
            color
        )
        if (seg) traces.push(seg)
    })

    if (traces.length === 0) return null

    const data = applyHomeysStandardTraceHover(traces, {
        yAxisLabel: yLabel,
    })

    const tickDayStarts = [0, 24, 48, 72, 96, 120, 144]

    const layoutMerged = buildHomeysStandardPlotlyLayout(
        {
            autosize: true,
            showlegend: true,
            legend: { orientation: "h", y: -0.22 },
            margin: { t: 16, r: 16, b: 80, l: 56 },
            xaxis: {
                type: "linear",
                range: [-0.5, WEEK_HOURLY_SLOTS - 0.5],
                tickmode: "array",
                tickvals: tickDayStarts,
                ticktext: [...WEEKDAY_LABELS_FR],
            },
            yaxis: { rangemode: "tozero" },
        },
        {
            x_label: "Semaine type (lun. → dim., par heure)",
            y_label: yLabel,
            name: chartBlock.name,
        },
        {}
    )

    return {
        data,
        layout: layoutMerged,
    }
}

/** @deprecated Même rendu que {@link buildPvDimensionnementSeasonHourlyWeekLineGraph}. */
export const buildPvDimensionnementSeasonWeekdayLineGraph =
    buildPvDimensionnementSeasonHourlyWeekLineGraph

/** @deprecated Utiliser {@link buildPvDimensionnementSeasonHourlyWeekLineGraph}. */
export const buildPvDimensionnementSeasonWeekdayBarGraph =
    buildPvDimensionnementSeasonHourlyWeekLineGraph
